import { ActiveSession } from '@/types/app.types';
import { groupLogsByExercise } from './session-flow';

/**
 * hydratePerformanceFromTemplate: Fresh ActiveSession from DB template.
 */
export const hydratePerformanceFromTemplate = (
    template: any, 
    dayId: string, 
    customDate: string | null, 
    sourceEventId: string | null, 
    isManual: boolean
): ActiveSession => {
    const activeBlocks = (template.blocks || [])
        .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));

    return {
        id: crypto.randomUUID(),
        startTime: customDate || new Date().toISOString(),
        program_day_id: dayId,
        p_consume_event_id: sourceEventId || undefined,
        sessionFocus: template.session_focus,
        isManualMode: isManual,
        blocks: activeBlocks.map((b: any) => ({
            id: b.id,
            label: b.label,
            block_type: b.block_type,
            items: (b.block_items || []).sort((a: any, b: any) => a.sort_order - b.sort_order).map((bi: any) => ({
                id: bi.id,
                exercise_library_id: bi.exercise_library_id,
                name: bi.exercise_library.name,
                target_sets: bi.target_sets,
                target_reps: bi.target_reps,
                target_weight: bi.target_weight,
                target_rpe: bi.target_rpe,
                tempo: bi.tempo,
                metric_type: bi.metric_type,
                set_targets: bi.set_targets
            }))
        })),
        logs: {}
    };
};

/**
 * hydrateSessionFromHistory: Hydrates an activeSession structure from historical logs for editing.
 */
export const hydrateSessionFromHistory = (logs: any[], header: any): ActiveSession => {
    const groups: any[] = [];
    logs.forEach(log => {
        const exName = log.exercise_library?.name || log.exercise_name_snapshot || 'Unknown Exercise';
        const lastGroup = groups[groups.length - 1];
        
        if (!lastGroup || lastGroup.name !== exName) {
            groups.push({ 
                id: log.block_item_id,
                name: exName,
                sets: [log] 
            });
        } else {
            lastGroup.sets.push(log);
        }
    });

    return {
        id: header.id,
        startTime: header.start_time,
        endTime: header.end_time,
        program_day_id: header.program_day_id,
        p_consume_event_id: header.event_id || header.id,
        isEditMode: true,
        blocks: [{
            id: 'history-edit-block',
            label: 'Session History',
            block_type: 'LINEAR',
            items: groups.map(g => ({
                id: g.id,
                name: g.name,
                target_sets: g.sets.length,
                target_reps: '?',
                target_weight: '?',
                target_rpe: '?',
                tempo: ''
            }))
        }],
        logs: groups.reduce((acc, g) => {
            acc[g.id] = g.sets.map((s: any) => ({
                id: s.id,
                weight: s.weight,
                reps: s.reps,
                rpe: s.rpe,
                duration_seconds: s.duration_seconds,
                distance_meters: s.distance_meters
            }));
            return acc;
        }, {} as Record<string, any[]>)
    };
};
